/**
 * Hook para registrar eventos de comportamento do usuário
 * 
 * Taxonomia oficial de eventos para o motor de maturidade
 * Eventos são usados para calcular behavior_score via v_user_maturity_signals_30d
 */

import { useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

// ==================== TIPOS DE EVENTOS ====================

export type BehaviorEventType =
  // Sessão / Navegação
  | 'SESSION_START'
  | 'SESSION_END'
  | 'PAGE_VIEW'
  | 'CHAT_OPEN'
  | 'CHAT_MESSAGE_SENT'
  | 'CHAT_MESSAGE_RECEIVED'
  // PIN / Plano
  | 'PIN_OPEN'
  | 'PIN_SECTION_OPEN'
  | 'PIN_UPDATE_CLICKED'
  | 'PIN_UPDATE_CONFIRMED'
  | 'PIN_UPDATE_CANCELLED'
  // Ações executáveis
  | 'ACTION_CREATED'
  | 'ACTION_STATUS_CHANGED'
  | 'ACTION_DONE'
  | 'ACTION_BLOCKED'
  | 'ACTION_DELETED'
  // Onboarding / Anamnese / Maturidade
  | 'ONBOARDING_STARTED'
  | 'ONBOARDING_COMPLETED'
  | 'ANAMNESE_STARTED'
  | 'ANAMNESE_SUBMITTED'
  | 'MATURITY_REASSESSMENT_PROMPTED'
  | 'MATURITY_REASSESSMENT_ACCEPTED'
  | 'MATURITY_REASSESSMENT_COMPLETED'
  // Financeiro / Realismo
  | 'FINANCIAL_SNAPSHOT_UPDATED'
  | 'FINANCIAL_GATE_BLOCKED'
  // Check-in / Retenção
  | 'CHECKIN_PROMPTED'
  | 'CHECKIN_ANSWERED'
  | 'NUDGE_SENT'
  | 'NUDGE_OPENED';

// ==================== ESTRUTURA DO PAYLOAD ====================

export interface BehaviorEventMeta {
  source?: 'ui' | 'system' | 'ai';
  version?: string;
}

export interface BehaviorEventPayload {
  request_id?: string;
  page?: string;
  component?: string;
  pin_plan_id?: string;
  pin_section_key?: string;
  action_id?: string;
  conversation_id?: string;
  message_length?: number;
  actions_count?: number;
  from_status?: string;
  to_status?: string;
  estimated_cost_brl?: number;
  cash_available_brl_bucket?: string;
  weekly_hours_bucket?: string;
  source?: string;
  blocker_type?: string;
  meta?: BehaviorEventMeta;
  [key: string]: string | number | boolean | null | undefined | BehaviorEventMeta;
}

export interface BehaviorSignals30d {
  sessions_30d: number;
  actions_done_30d: number;
  actions_created_30d: number;
  pin_opens_30d: number;
  chat_opens_30d: number;
  docs_uploaded_30d: number;
  execution_rate_30d: number;
  last_activity_at: string | null;
  calculated_at: string;
}

// ==================== HOOK ====================

export function useBehaviorEvents() {
  const { user } = useAuth();
  const requestIdRef = useRef<string | null>(null);

  // Gerar ou reutilizar request_id da sessão
  const getRequestId = useCallback(() => {
    if (!requestIdRef.current) {
      requestIdRef.current = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    return requestIdRef.current;
  }, []);

  // Pegar página atual
  const getCurrentPage = useCallback(() => {
    return typeof window !== 'undefined' ? window.location.pathname : '/';
  }, []);

  /**
   * Registrar um evento de comportamento
   */
  const logEvent = useCallback(
    async (
      eventType: BehaviorEventType,
      payload: BehaviorEventPayload = {}
    ): Promise<string | null> => {
      if (!user) return null;

      try {
        // Enriquecer payload com dados padrão
        const enrichedPayload: BehaviorEventPayload = {
          request_id: getRequestId(),
          page: getCurrentPage(),
          meta: {
            source: 'ui',
            version: 'web-1.0.0',
          },
          ...payload,
        };

        // Inserir diretamente na tabela
        const { data, error } = await (supabase as any)
          .from('user_behavior_events')
          .insert({
            user_id: user.id,
            event_type: eventType,
            payload: enrichedPayload,
          })
          .select('id')
          .single();

        if (error) {
          console.error('Erro ao registrar evento:', error);
          return null;
        }

        return data?.id ?? null;
      } catch (error) {
        console.error('Erro ao registrar evento:', error);
        return null;
      }
    },
    [user, getRequestId, getCurrentPage]
  );

  // ==================== ATALHOS: SESSÃO ====================

  const logSessionStart = useCallback(
    () => logEvent('SESSION_START', { 
      timestamp: new Date().toISOString() 
    }),
    [logEvent]
  );

  const logSessionEnd = useCallback(
    () => logEvent('SESSION_END', { 
      timestamp: new Date().toISOString() 
    }),
    [logEvent]
  );

  const logPageView = useCallback(
    (page?: string, component?: string) => logEvent('PAGE_VIEW', { 
      page: page ?? getCurrentPage(),
      component 
    }),
    [logEvent, getCurrentPage]
  );

  // ==================== ATALHOS: CHAT ====================

  const logChatOpen = useCallback(
    (conversationId?: string) =>
      logEvent('CHAT_OPEN', { conversation_id: conversationId }),
    [logEvent]
  );

  const logChatMessageSent = useCallback(
    (conversationId?: string, messageLength?: number) =>
      logEvent('CHAT_MESSAGE_SENT', { 
        conversation_id: conversationId,
        message_length: messageLength 
      }),
    [logEvent]
  );

  const logChatMessageReceived = useCallback(
    (conversationId?: string) =>
      logEvent('CHAT_MESSAGE_RECEIVED', { conversation_id: conversationId }),
    [logEvent]
  );

  // ==================== ATALHOS: PIN ====================

  const logPinOpen = useCallback(
    (pinPlanId?: string) => logEvent('PIN_OPEN', { pin_plan_id: pinPlanId }),
    [logEvent]
  );

  const logPinSectionOpen = useCallback(
    (pinSectionKey: string, pinPlanId?: string) =>
      logEvent('PIN_SECTION_OPEN', { 
        pin_section_key: pinSectionKey, 
        pin_plan_id: pinPlanId 
      }),
    [logEvent]
  );

  const logPinUpdateClicked = useCallback(
    (pinSectionKey: string) =>
      logEvent('PIN_UPDATE_CLICKED', { pin_section_key: pinSectionKey }),
    [logEvent]
  );

  const logPinUpdateConfirmed = useCallback(
    (pinSectionKey: string, actionsCount: number) =>
      logEvent('PIN_UPDATE_CONFIRMED', { 
        pin_section_key: pinSectionKey, 
        actions_count: actionsCount 
      }),
    [logEvent]
  );

  const logPinUpdateCancelled = useCallback(
    (pinSectionKey: string) =>
      logEvent('PIN_UPDATE_CANCELLED', { pin_section_key: pinSectionKey }),
    [logEvent]
  );

  // ==================== ATALHOS: AÇÕES ====================

  const logActionCreated = useCallback(
    (actionId: string, options?: { 
      pinSectionKey?: string; 
      estimatedCostBrl?: number;
      title?: string;
    }) =>
      logEvent('ACTION_CREATED', { 
        action_id: actionId, 
        pin_section_key: options?.pinSectionKey,
        estimated_cost_brl: options?.estimatedCostBrl,
      }),
    [logEvent]
  );

  const logActionStatusChanged = useCallback(
    (actionId: string, fromStatus: string, toStatus: string) =>
      logEvent('ACTION_STATUS_CHANGED', { 
        action_id: actionId, 
        from_status: fromStatus, 
        to_status: toStatus 
      }),
    [logEvent]
  );

  const logActionDone = useCallback(
    (actionId: string) =>
      logEvent('ACTION_DONE', { action_id: actionId }),
    [logEvent]
  );

  const logActionBlocked = useCallback(
    (actionId: string, blockerType?: string) =>
      logEvent('ACTION_BLOCKED', { 
        action_id: actionId, 
        blocker_type: blockerType 
      }),
    [logEvent]
  );

  const logActionDeleted = useCallback(
    (actionId: string) =>
      logEvent('ACTION_DELETED', { action_id: actionId }),
    [logEvent]
  );

  // ==================== ATALHOS: ONBOARDING / ANAMNESE ====================

  const logOnboardingStarted = useCallback(
    () => logEvent('ONBOARDING_STARTED'),
    [logEvent]
  );

  const logOnboardingCompleted = useCallback(
    () => logEvent('ONBOARDING_COMPLETED'),
    [logEvent]
  );

  const logAnamneseStarted = useCallback(
    () => logEvent('ANAMNESE_STARTED'),
    [logEvent]
  );

  const logAnamneseSubmitted = useCallback(
    (source: 'onboarding' | 'manual' | 'reassessment' | 'maturity_tab') =>
      logEvent('ANAMNESE_SUBMITTED', { source }),
    [logEvent]
  );

  const logMaturityReassessmentPrompted = useCallback(
    () => logEvent('MATURITY_REASSESSMENT_PROMPTED'),
    [logEvent]
  );

  const logMaturityReassessmentAccepted = useCallback(
    () => logEvent('MATURITY_REASSESSMENT_ACCEPTED'),
    [logEvent]
  );

  const logMaturityReassessmentCompleted = useCallback(
    () => logEvent('MATURITY_REASSESSMENT_COMPLETED'),
    [logEvent]
  );

  // ==================== ATALHOS: FINANCEIRO ====================

  const logFinancialSnapshotUpdated = useCallback(
    (cashBucket: string, hoursBucket: string) =>
      logEvent('FINANCIAL_SNAPSHOT_UPDATED', { 
        cash_available_brl_bucket: cashBucket,
        weekly_hours_bucket: hoursBucket 
      }),
    [logEvent]
  );

  const logFinancialGateBlocked = useCallback(
    (reason?: string) =>
      logEvent('FINANCIAL_GATE_BLOCKED', { blocker_type: reason }),
    [logEvent]
  );

  // ==================== ATALHOS: CHECK-IN ====================

  const logCheckinPrompted = useCallback(
    () => logEvent('CHECKIN_PROMPTED'),
    [logEvent]
  );

  const logCheckinAnswered = useCallback(
    (blockerType?: string) =>
      logEvent('CHECKIN_ANSWERED', { blocker_type: blockerType }),
    [logEvent]
  );

  const logNudgeSent = useCallback(
    () => logEvent('NUDGE_SENT'),
    [logEvent]
  );

  const logNudgeOpened = useCallback(
    () => logEvent('NUDGE_OPENED'),
    [logEvent]
  );

  // ==================== BUSCAR SINAIS 30D ====================

  const fetchBehaviorSignals = useCallback(async (): Promise<BehaviorSignals30d | null> => {
    if (!user) return null;

    try {
      const { data, error } = await (supabase as any)
        .from('v_user_maturity_signals_30d')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Erro ao buscar sinais de comportamento:', error);
        return null;
      }

      if (!data) return null;

      return {
        sessions_30d: data.sessions_30d ?? 0,
        actions_done_30d: data.actions_done_30d ?? 0,
        actions_created_30d: data.actions_created_30d ?? 0,
        pin_opens_30d: data.pin_returns_30d ?? 0,
        chat_opens_30d: 0,
        docs_uploaded_30d: 0,
        execution_rate_30d: data.execution_rate_30d ?? 0,
        last_activity_at: null,
        calculated_at: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Erro ao buscar sinais:', error);
      return null;
    }
  }, [user]);

  return {
    // Core
    logEvent,
    getRequestId,
    
    // Sessão
    logSessionStart,
    logSessionEnd,
    logPageView,
    
    // Chat
    logChatOpen,
    logChatMessageSent,
    logChatMessageReceived,
    
    // PIN
    logPinOpen,
    logPinSectionOpen,
    logPinUpdateClicked,
    logPinUpdateConfirmed,
    logPinUpdateCancelled,
    
    // Ações
    logActionCreated,
    logActionStatusChanged,
    logActionDone,
    logActionBlocked,
    logActionDeleted,
    
    // Onboarding / Anamnese
    logOnboardingStarted,
    logOnboardingCompleted,
    logAnamneseStarted,
    logAnamneseSubmitted,
    logMaturityReassessmentPrompted,
    logMaturityReassessmentAccepted,
    logMaturityReassessmentCompleted,
    
    // Financeiro
    logFinancialSnapshotUpdated,
    logFinancialGateBlocked,
    
    // Check-in
    logCheckinPrompted,
    logCheckinAnswered,
    logNudgeSent,
    logNudgeOpened,
    
    // Sinais
    fetchBehaviorSignals,
  };
}
